import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Quote } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const INPUT = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-4 py-2.5 text-sm";

export const BannerMessagePreview = ({ message, reference, testid = "portal-banner-message" }) => (
  <div data-testid={testid} className="mt-5 flex items-start gap-2.5 rounded-xl px-4 py-3 max-w-2xl" style={{ background: "rgba(255,255,255,0.10)", borderLeft: "3px solid var(--sj-accent)" }}>
    <Quote className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-accent)" }} />
    <div className="text-sm sm:text-[15px] text-white/95 leading-relaxed">
      <span className="italic">{message}</span>
      {reference && <span className="ml-1.5 font-semibold not-italic" style={{ color: "var(--sj-accent)" }}>— {reference}</span>}
    </div>
  </div>
);

export const BannerMessageSettings = () => {
  const [form, setForm] = useState({ message: "", reference: "", enabled: true });
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/portal-banner").then(({ data }) => { setForm({ message: data.message, reference: data.reference, enabled: data.enabled }); setMeta(data); }).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/portal-banner", form); setMeta(data); toast.success(form.enabled && form.message ? "Banner message is now showing on every member's portal" : "Banner message saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className={`${CARD} p-6 space-y-4`} data-testid="banner-message-card">
      <div>
        <h2 className="font-head text-xl font-semibold flex items-center gap-2" style={{ color: "var(--sj-text)" }}>
          <Quote className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Seasonal banner message
        </h2>
        <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>A short welcome, theme or verse shown on every member's portal banner.</p>
      </div>
      <div>
        <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Message <span style={{ color: "var(--sj-text-3)" }}>({240 - form.message.length} characters left)</span></label>
        <textarea data-testid="banner-message-input" rows={3} maxLength={240} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="e.g. The Lord is my shepherd; I shall not want." className={`${INPUT} mt-1 resize-none`} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 items-end">
        <div>
          <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Verse reference or sign-off <span style={{ color: "var(--sj-text-3)" }}>(optional)</span></label>
          <input data-testid="banner-reference-input" maxLength={60} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Psalm 23:1" className={`${INPUT} mt-1`} />
        </div>
        <label className="flex items-center gap-2.5 text-sm cursor-pointer pb-2.5" style={{ color: "var(--sj-text-2)" }}>
          <input data-testid="banner-enabled-toggle" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
          Show on member portals
        </label>
      </div>
      {form.message.trim() && (
        <div className="sj-hero p-5" data-testid="banner-message-preview-wrap">
          <div className="sj-eyebrow">Preview</div>
          <div className="font-head text-xl font-extrabold mt-1">Welcome, <span style={{ color: "var(--sj-accent)" }}>Member</span>!</div>
          <BannerMessagePreview message={form.message.trim()} reference={form.reference.trim()} testid="banner-message-preview" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="save-banner-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save banner message"}
        </button>
        {meta.updated_by && <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Last updated by {meta.updated_by}{meta.updated_at ? ` on ${new Date(meta.updated_at).toLocaleDateString("en-GB")}` : ""}</span>}
      </div>
    </div>
  );
};
